import {useMemo} from 'react'
import {TKey} from '@/translations/i18n'
import {DiseaseExtended, TreatmentRequestScore} from '@/features/treatment-requests/types'
import {ObjectKeys} from '@/utilities/helpers'
import {adultsScoresMap, evolutionaryAgeScoresMap, Score} from '@/features/treatment-requests/utils'
import {PatientAgeRange} from '@/types'
import {z} from 'zod'

interface UseTreatmentRequestScoresProps {
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
    existingScores?: z.infer<typeof TreatmentRequestScore>[]
    diseases?: z.infer<typeof DiseaseExtended>[]
}

interface UseTreatmentRequestScoresReturn {
    scoresMap: Record<string, {label: TKey; scores: Score[]}>
    defaultScores: z.infer<typeof TreatmentRequestScore>[]
}

export const useTreatmentRequestScores = ({
    serviceType,
    existingScores,
    diseases
}: UseTreatmentRequestScoresProps): UseTreatmentRequestScoresReturn => {
    const scoresMap = useMemo((): Partial<Record<TreatmentRequestScore['type'], {label: TKey; scores: Score[]}>> => {
        if (serviceType == 'adults') {
            const scoresKeys = ObjectKeys(adultsScoresMap)
            const canDisplaySubType = diseases?.some(disease => disease.name == 'I° Gruppo')
            return scoresKeys.reduce(
                (acc, scoreType) => {
                    if (scoreType == 'subtype' && !canDisplaySubType) {
                        return acc
                    }
                    acc[scoreType] = adultsScoresMap[scoreType]
                    return acc
                },
                {} as Partial<Record<TreatmentRequestScore['type'], {label: TKey; scores: Score[]}>>
            )
        }

        return evolutionaryAgeScoresMap
    }, [serviceType, diseases])

    const defaultScores = useMemo(() => {
        if (existingScores) {
            // Filter out subtype scores if diseases don't include "I° Gruppo"
            const canDisplaySubType = diseases?.some(disease => disease.name === 'I° Gruppo')
            return existingScores.filter(score => {
                if (score.type === 'subtype' && !canDisplaySubType) {
                    return false
                }
                return true
            })
        }

        return ObjectKeys(scoresMap).map(scoreType => ({
            type: scoreType,
            score: null
        }))
    }, [existingScores, scoresMap, diseases])

    return {
        scoresMap,
        defaultScores
    }
}
